import {Fragment, useContext, useEffect, useState} from 'react';
import {ActivityIndicator, Headline} from 'react-native-paper';

import {useQuery, useQueryClient} from 'react-query';

import analytics from '@react-native-firebase/analytics';
import auth, {FirebaseAuthTypes} from '@react-native-firebase/auth';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';
import crashlytics from '@react-native-firebase/crashlytics';

import {useIsMounted} from '../hooks/useIsMounted';
import {useAppSettings} from './AppSettings';
import {PersonInfoContext} from '../signed-in/Stack';
import {StyleSheet, View} from 'react-native';
import {Person, PersonRoles} from '../hooks/usePersonInfo';
import {fetchAuthPersonInfo} from '../hooks/usePersonInfo';

export const FirestoreUserGate = (props: {children: JSX.Element}) => {
  const authUser = auth().currentUser;
  // console.log('FirebaseUserGate - authUser: ' + authUser?.uid);

  const [gating, setGating] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [waitingToShowLoading, setWaitingToShowLoading] = useState(true);
  const queryClient = useQueryClient();
  const userQuery = useQuery(
    ['authPersonInfo', auth().currentUser],
    fetchAuthPersonInfo,
  );
  const appSettings = useAppSettings();
  const personInfo = useContext(PersonInfoContext);
  const isMounted = useIsMounted();

  useEffect(() => {
    if (!authUser || !isMounted()) {
      return;
    }
    // console.log(
    //   `FirebaseUserGate ${gating ? 'gating' : 'passing'} ${
    //     userQuery.isFetching ? 'fetching' : 'settled'
    //   } user ${userQuery.data?.person.authUid}`,
    // );

    // If we are gating, is our query settled? we may be able to act
    if (gating && !userQuery.isFetching) {
      if (userQuery.isError) {
        // TODO handle the error case for the query
        console.log('FirebaseUserGate, query error? ', userQuery.error);
        return;
      }
      if (userQuery.isSuccess && userQuery.data !== undefined) {
        const user = userQuery.data.person;
        // console.log(`FirebaseUserGate pass: ${user.authUid}`);

        // breadcrumbs out to the cloud...
        analytics().setUserProperties({
          authUid: userQuery.data.person.authUid,
          email: userQuery.data.person.email ?? 'unknown',
        });

        crashlytics().setUserId(user.authUid);
        crashlytics().log('FirestoreUserGate::firestore user load complete');

        personInfo?.setFilteredPersonInfo({
          authPerson: {
            person: user,
            isAdmin: user.roles?.includes(PersonRoles.Admin) || false,
            isGuestRelations:
              user.roles?.includes(PersonRoles.GuestRelations) || false,
            isMember: user.roles?.includes(PersonRoles.Member) || false,
          },
        });

        // Let the user through
        setGating(false);
        setWaitingToShowLoading(true);
        return;
      }

      if (userQuery.isSuccess && userQuery.data === undefined) {
        // console.log('FirebaseUserGate no user');
        if (!creatingUser) {
          // console.log('FirebaseUserGate - no one else creating user');
          setCreatingUser(true);
          let newUser = copyAuthUsertoFirestore(authUser);

          firestore()
            .collection('users')
            .add(newUser)
            .then(newUserDoc => {
              newUser.ref =
                newUserDoc as FirebaseFirestoreTypes.DocumentReference<Person>;
              queryClient.setQueryData(
                ['authPersonInfo', newUser.authUid],
                newUser,
              );
              userQuery.refetch();
              analytics().logEvent('successCreatedUser');
            })
            .catch(e => {
              console.log('FirebaseUserGate user create fail', e);
            });
        }
      }
    }
  }, [
    gating,
    userQuery,
    creatingUser,
    authUser,
    queryClient,
    personInfo,
    isMounted,
  ]);

  if (gating && !timedOut) {
    setTimeout(() => {
      if (gating && isMounted()) {
        setWaitingToShowLoading(false);
      }
    }, 2000);
    setTimeout(() => {
      if (gating && isMounted()) {
        setTimedOut(true);
      }
    }, 30000);

    return (
      <View
        style={[
          styles.loadingContainer,
          {backgroundColor: appSettings.currentTheme.colors.background},
        ]}>
        {!waitingToShowLoading && (
          <Fragment>
            <Headline
              style={[
                styles.padded,
                {color: appSettings.currentTheme.colors.text},
              ]}>
              {appSettings.t('loading')}...
            </Headline>
            <ActivityIndicator
              size={'large'}
              theme={{
                ...appSettings.currentTheme,
                colors: {primary: appSettings.currentTheme.colors.accent},
              }}
            />
          </Fragment>
        )}
      </View>
    );
  }

  if (gating && timedOut) {
    return (
      <View
        style={[
          styles.loadingContainer,
          {backgroundColor: appSettings.currentTheme.colors.background},
        ]}>
        {!waitingToShowLoading && (
          <Fragment>
            <Headline
              style={[
                styles.padded,
                {color: appSettings.currentTheme.colors.text},
              ]}>
              {appSettings.t('unknownError')}
            </Headline>
          </Fragment>
        )}
      </View>
    );
  }

  return <>{props.children}</>;
};

// We want the source of truth to be the firestore user, copy relevant auth bits in
const copyAuthUsertoFirestore = (
  authUser: FirebaseAuthTypes.User,
  firestoreUser?: Partial<Person>,
  additionalUserInfo?: FirebaseAuthTypes.AdditionalUserInfo,
): Partial<Person> => {
  // console.log(
  //   'FirestoreUserGate::copyAuthUsertoFirestore with user/userinfo:',
  //   JSON.stringify(authUser, null, 2),
  //   JSON.stringify(firestoreUser, null, 2),
  //   JSON.stringify(additionalUserInfo, null, 2),
  // );
  if (firestoreUser === undefined) {
    firestoreUser = {
      authUid: authUser.uid,
    };
  }

  if (authUser.email && !firestoreUser.email) {
    firestoreUser.email = authUser.email.toLowerCase();
  }
  if (authUser.phoneNumber && !firestoreUser.phoneNumber1) {
    firestoreUser.phoneNumber1 = authUser.phoneNumber;
  }
  if (authUser.uid && !firestoreUser.authUid) {
    firestoreUser.authUid = authUser.uid;
  }

  // Maybe we have additional user info?
  if (additionalUserInfo?.profile) {
    if (
      !firestoreUser.firstName &&
      additionalUserInfo.profile.hasOwnProperty('first_name')
    ) {
      firestoreUser.firstName = additionalUserInfo.profile.first_name;
    }
    if (
      !firestoreUser.firstName &&
      additionalUserInfo.profile.hasOwnProperty('given_name')
    ) {
      firestoreUser.firstName = additionalUserInfo.profile.given_name;
    }
    // console.log(
    //   `FirestoreUserGate::copyAuthUsertoFirestore first name? ${firestoreUser.firstName}`,
    // );
    if (
      !firestoreUser.lastName &&
      additionalUserInfo.profile.hasOwnProperty('last_name')
    ) {
      firestoreUser.lastName = additionalUserInfo.profile.last_name;
    }
    if (
      !firestoreUser.lastName &&
      additionalUserInfo.profile.hasOwnProperty('family_name')
    ) {
      firestoreUser.lastName = additionalUserInfo.profile.family_name;
    }
    // console.log(
    //   `FirestoreUserGate::copyAuthUsertoFirestore last name? ${firestoreUser.lastName}`,
    // );

    if (
      !authUser.photoURL &&
      additionalUserInfo.profile.hasOwnProperty('picture')
    ) {
      // console.log(
      //   'FirestoreUserGate::copyAuthUserToFirestore attempting profile photoURL update',
      // );
      authUser
        .updateProfile({
          photoURL: additionalUserInfo.profile.picture.data.url,
        })
        .catch(e =>
          console.log(
            'FirestoreUserGate::copyAuthusertoFirestore photo copy error: ',
            e,
          ),
        );
    }
    // console.log(
    //   `FirestoreUserGate::copyAuthUsertoFirestore photo URL? ${authUser.photoURL}`,
    // );
  }

  return firestoreUser;
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  padded: {
    padding: 40,
  },
});
